import React, { useState } from 'react';
import OneVerseLogo from '@/components/OneVerseLogo';
import { useUser } from '@/providers/UserProvider';
import { Button } from '@/components/ui/button';
import TOTPSetup from '@/components/TOTPSetup';
import SMSSetup from './SMSSetup';
import { API } from '@/utils/API';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, LogOut } from 'lucide-react';
import { logout } from '@/lib/logout';

type MFAMethod = 'totp' | 'sms';

const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <OneVerseLogo className="mb-6 mt-2 mx-auto" />
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">
            Multi-Factor Authentication Setup
          </h1>
          <p className="text-gray-600">
            Please choose your preferred method for multi-factor authentication
            to continue using the application.
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">{children}</div>
      </div>
    </div>
  );
};

const MultiFactorAuthGate: React.FC = () => {
  const { userData } = useUser();
  const smsSetupAvailable = userData?.mfa_sms_setup_available !== false;
  const [selectedMethod, setSelectedMethod] = useState<MFAMethod | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<
    'select-method' | 'totp-setup' | 'sms-setup'
  >('select-method');
  const [secret, setSecret] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [phoneNumberMasked, setPhoneNumberMasked] = useState<string>('');
  const handleMethodSelect = (method: MFAMethod) => {
    setSelectedMethod(method);
  };

  const onBack = () => {
    setStep('select-method');
    setError('');
    setSecret('');
  };

  const handleContinue = async () => {
    setIsLoading(true);
    const setup = selectedMethod === 'totp' ? setupTOTPMFA : setupSMSMFA;

    const [success, data] = await setup();
    if (!success) {
      setError(data);
      return setIsLoading(false);
    }

    if (selectedMethod === 'totp') {
      setSecret(data);
    } else if (selectedMethod === 'sms') {
      setPhoneNumberMasked(data);
    }

    setIsLoading(false);
    setStep(selectedMethod === 'totp' ? 'totp-setup' : 'sms-setup');
  };

  const setupSMSMFA = async (): Promise<[boolean, string]> => {
    const response = await API.authenticated.fetch('/users/phone_number', {
      method: 'GET',
    });
    const data = await response.json();
    return [true, data.phone_number];
  };

  const setupTOTPMFA = async (): Promise<[boolean, string]> => {
    const response = await API.authenticated.fetch('/mfa/totp', {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json();
      return [false, errorData.error || 'Failed to setup TOTP MFA'];
    }

    const data = await response.json();
    return [true, data.code];
  };

  if (step === 'totp-setup') {
    return (
      <Layout>
        <TOTPSetup onBack={onBack} secret={secret} />
      </Layout>
    );
  } else if (step === 'sms-setup') {
    return (
      <Layout>
        <SMSSetup onBack={onBack} phoneNumberMasked={phoneNumberMasked} />
      </Layout>
    );
  }

  return (
    <Layout>
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <h2 className="text-xl font-semibold text-gray-900 mb-6 mt-6">
        Select Authentication Method
      </h2>

      <div className="space-y-4">
        {/* TOTP Option */}
        <div
          className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
            selectedMethod === 'totp'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => handleMethodSelect('totp')}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  selectedMethod === 'totp'
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}
              >
                {selectedMethod === 'totp' && (
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                )}
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">
                Authenticator App (TOTP)
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Use an authenticator app like Google Authenticator, Authy, or
                1Password to generate time-based codes.
              </p>
            </div>
          </div>
        </div>

        {/* SMS Option */}
        {smsSetupAvailable && (
          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
              selectedMethod === 'sms'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleMethodSelect('sms')}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    selectedMethod === 'sms'
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}
                >
                  {selectedMethod === 'sms' && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  SMS Text Message
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Receive verification codes via text message to your registered
                  phone number.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Continue Button */}
      <div className="mt-8 flex justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={logout}
          className="flex items-center gap-2"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!selectedMethod || isLoading || step !== 'select-method'}
        >
          {isLoading ? 'Setting up...' : 'Continue'}
        </Button>
      </div>
    </Layout>
  );
};

export const MultiFactorAuthGateWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { userData, loading } = useUser();

  // Show loading while user data is being fetched
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Impersonation sessions bypass the MFA setup screen — the Hub
  // admin's goal is to see what comes after onboarding, not to be
  // blocked at the same step the impersonated user is stuck at.
  if (!userData?.mfa_configured && !userData?.impersonating) {
    return <MultiFactorAuthGate />;
  }

  // Otherwise, show the normal app content
  return <>{children}</>;
};

export default MultiFactorAuthGate;
